import React from 'react';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { IEpisode, EpisodeStatus } from '@packages/global';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LazyImage from '../common/LazyImage';

interface EpisodeGridProps {
  episodes: IEpisode[];
  loading: boolean;
  onCreateEpisode: () => void;
  onEditEpisode: (episode: IEpisode) => void;
  onDeleteEpisode: (id: string) => void;
}

export default function EpisodeGrid({
  episodes,
  loading,
  onCreateEpisode,
  onEditEpisode,
  onDeleteEpisode
}: EpisodeGridProps) {
  const { t, i18n } = useTranslation(['project', 'common']);
  const navigate = useNavigate();

  const handleEpisodeClick = (episode: IEpisode) => {
    if (episode.status === EpisodeStatus.DRAFT) {
      navigate(`/episode/${episode.id}/script-generation`);
    } else {
      navigate(`/episode/${episode.id}/editor`);
    }
  };

  const getStatusColor = (status: EpisodeStatus) => {
    switch (status) {
      case EpisodeStatus.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case EpisodeStatus.STORYBOARD_GENERATION:
        return 'bg-primary/20 text-primary';
      case EpisodeStatus.IMAGE_GENERATION:
        return 'bg-purple-500/20 text-purple-400';
      case EpisodeStatus.VOICE_GENERATION:
        return 'bg-primary/20 text-primary';
      // case EpisodeStatus.SOUND_GENERATION:
      //   return 'bg-pink-100 text-pink-800';
      case EpisodeStatus.VIDEO_GENERATION:
        return 'bg-yellow-100 text-yellow-800';
      case EpisodeStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-12 ">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-400">{t('common:loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,256px)] justify-between gap-x-4 gap-y-6">
      {episodes.map((episode) => (
        <div
          key={episode.id}
          onClick={() => handleEpisodeClick(episode)}
          className="bg-bg-surface w-64 rounded-xl border border-border-subtle shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer hover:border-primary aspect-[3/4] overflow-hidden group flex-shrink-0"
        >
          {episode.thumbnailUrl ? (
            <div className="relative h-full">
              <LazyImage
                src={episode.thumbnailUrl}
                alt={episode.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                placeholderClassName="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-lg font-semibold text-white mb-1 truncate">{episode.title}</h3>
                {episode.description && (
                  <p className="text-gray-300 text-sm line-clamp-2 mb-2">{episode.description}</p>
                )}
                <div className="flex items-center justify-between">
                  {/* <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(episode.status)}`}>
                    {episode.status}
                  </span> */}
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditEpisode(episode);
                      }}
                      className="p-2 rounded-lg bg-black/20 hover:bg-black/40 text-white/80 hover:text-white transition-all backdrop-blur-sm"
                      title={t('common:edit')}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteEpisode(episode.id);
                      }}
                      className="p-2 rounded-lg bg-black/20 hover:bg-red-500/80 text-white/80 hover:text-white transition-all backdrop-blur-sm"
                      title={t('common:delete')}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 flex flex-col h-full">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-50 mb-2">{episode.title}</h3>
                </div>
              </div>

              {episode.description && (
                <p className="text-gray-400 mb-4 line-clamp-3 flex-1">{episode.description}</p>
              )}

              <div className="mt-auto">
                <div className="text-sm text-gray-500 mb-4">
                  {t('project:createdAt')}: {new Date(episode.createdAt).toLocaleDateString(i18n.language)}
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-border-subtle">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEpisode(episode);
                    }}
                    className="p-2 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all"
                    title={t('common:edit')}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteEpisode(episode.id);
                    }}
                    className="p-2 rounded-lg bg-bg-elevated hover:bg-red-500 text-gray-300 hover:text-white transition-all"
                    title={t('common:delete')}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      <div
        onClick={onCreateEpisode}
        className="bg-bg-surface w-64 rounded-xl border-2 border-dashed border-border-default hover:border-primary shadow-sm hover:shadow-lg transition-all cursor-pointer group p-6 flex flex-col justify-center aspect-[3/4] flex-shrink-0"
      >
        <div className="text-center">
          <PlusIcon className="w-12 h-12 text-gray-500 group-hover:text-primary mx-auto mb-4 transition-colors" />
          <h3 className="text-lg font-medium text-gray-300 group-hover:text-gray-50 mb-2 transition-colors">{t('project:episode.createNew')}</h3>
          <p className="text-sm text-gray-500 group-hover:text-gray-400 transition-colors">{t('project:episode.createNewDesc')}</p>
        </div>
      </div>
    </div>
  );
}